// Ambient wildlife loops: forest birdsong by day, crickets at night. Exactly one
// of the two streams plays at a time, crossfaded as the sun crosses golden hour;
// both fall silent under the overcast sky. A single "Ambient" toggle in the
// settings drawer (Sound section) turns whichever is playing off.
//
// Like soundtrack.js this rides on looping HTMLAudioElements (the big WAVs stream
// without a full WebAudio decode), crossfaded with gsap, and depends one-
// directionally on audio.js (prefs store + first-gesture unlock). main.js pulls
// it in and also feeds it the live sun elevation + overcast flag each frame via
// updateAmbient(); audio prefs are LOCAL (never part of the share/save payload).

import { onFirstGesture, onAmbientSettingChange, onVisibilityChange, getPrefs } from './audio.js';

// Logical name -> looping file (root-relative, resolved against index.html).
const TRACKS = {
  birds:    'assets/sounds/forest-birds.wav',
  crickets: 'assets/sounds/crickets-at-night.wav',
};

const VOL = 0.35;   // base loop volume (sits under the music bed); 0 when muted/off
const FADE = 2.5;  // day<->night crossfade seconds (matches the soundtrack)

// Sun-elevation (degrees) handoff between birds (day) and crickets (night), with
// a hysteresis band so the sun hovering around golden hour can't flip-flop the
// loops. Dawn (~4.5°) → birds; golden hour (~2.45°) and below → crickets.
const BIRD_ABOVE = 5;
const CRICKET_BELOW = 3;
const MID = (BIRD_ABOVE + CRICKET_BELOW) / 2;

const DECIDE_INTERVAL = 0.5; // s — throttle the per-frame time-of-day decision

let started = false;     // have we passed the first-gesture audio unlock yet
let activeName = null;    // which loop is currently faded up (null = silence)
let desired = null;       // what the sky currently wants (independent of enabled/mute)
let lastDecideAt = -Infinity;
const els = {};           // name -> HTMLAudioElement (built lazily, kept for reuse)

// Element volume target: VOL unless muted or the Ambient toggle is off.
function targetVolume() {
  const p = getPrefs();
  return (p.muted || !p.ambientEnabled) ? 0 : VOL;
}

function getEl(name) {
  if (els[name]) return els[name];
  const a = new Audio(TRACKS[name]);
  a.preload = 'auto';
  a.loop = true;
  a.volume = 0;
  els[name] = a;
  return a;
}

// Crossfade to `name` (or null for silence): fade the old loop down + pause, the
// new one up to the current target volume. No-op if already on that loop.
function crossfadeTo(name) {
  if (name === activeName) return;
  const prev = activeName ? els[activeName] : null;
  activeName = name;
  if (prev) gsap.to(prev, { volume: 0, duration: FADE, onComplete: () => prev.pause() });
  if (!name) return;
  const a = getEl(name);
  a.play().then(() => {
    gsap.to(a, { volume: targetVolume(), duration: FADE });
  }).catch(() => { /* can reject if we beat the gesture unlock — retried below */ });
}

// Pick which loop the sky wants for a sun elevation, with hysteresis off the
// current loop; overcast silences both.
function decide(el, overcast, current) {
  if (overcast) return null;
  if (current === 'birds')    return el <= CRICKET_BELOW ? 'crickets' : 'birds';
  if (current === 'crickets') return el >= BIRD_ABOVE ? 'birds' : 'crickets';
  return el >= MID ? 'birds' : 'crickets'; // from silence: split at the midpoint
}

// Bring playback in line with `desired` + the current prefs. Retargets the live
// loop's volume too, so a mute/unmute while a loop plays glides instead of jumps.
function applyDesired() {
  if (!started || !getPrefs().ambientEnabled) return;
  if (desired === activeName) {
    // Already on the right loop (or both silent): just retarget the live volume
    // so a mute/unmute glides quickly instead of triggering a full crossfade.
    if (activeName) gsap.to(els[activeName], { volume: targetVolume(), duration: 0.4 });
  } else {
    crossfadeTo(desired); // time-of-day handoff — fade over FADE
  }
}

// Fade out whatever is playing (the Ambient toggle turned off).
function stopAll() {
  if (!activeName) return;
  const a = els[activeName];
  activeName = null;
  gsap.to(a, { volume: 0, duration: FADE, onComplete: () => a.pause() });
}

// Per-frame from main.js: decide birds/crickets/silence from the live sun
// elevation (deg) + overcast flag, throttled, and crossfade toward it.
export function updateAmbient(elevationDeg, overcast) {
  const t = performance.now() * 0.001;
  if (t - lastDecideAt < DECIDE_INTERVAL) return;
  lastDecideAt = t;

  const next = decide(elevationDeg, overcast, desired);
  if (next === desired) return;
  desired = next;
  applyDesired();
}

// Start the first loop at the first user gesture (the earliest browsers allow
// audio); `desired` is usually already set by the running animate() loop.
onFirstGesture(() => {
  started = true;
  applyDesired();
});

// Pause the active wildlife loop while the tab is hidden, resume on return (a
// backgrounded tab is expected to go quiet). The loop's position/volume are
// preserved, so it picks straight back up where it left off.
onVisibilityChange((hidden) => {
  if (!activeName) return;
  const a = els[activeName];
  if (!a) return;
  if (hidden) a.pause();
  else if (getPrefs().ambientEnabled) a.play().catch(() => { /* retried on next gesture */ });
});

// React live to the drawer: turning Ambient off fades out; turning it on (or a
// mute/unmute) resumes/retargets whatever the sky currently wants.
onAmbientSettingChange((p) => {
  if (!p.ambientEnabled) stopAll();
  else applyDesired();
});
